import React, { useCallback, useEffect } from "react";

import { colors } from "@zapalacorp/tokens";
import { TFunction, useTranslation } from "react-i18next";

import IconDownFile from "../../../../../../../../components/Generic/Icons/IconDownloadFile";
import { OutlinedWrapper } from "../../../../../../../../components/Generic/OutlinedWrapper";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import { useUploadCentreAction } from "../../../../../../../../hooks/useUploadCentreAction";
import { UploadCentreItemName, UploadCentreUploadLabel } from "../../../../../../../../util/enums";
import env from "../../../../../../../../util/env";
import { UploadItem } from "../UploadItem";
import { ColumnsValues } from "../UploadModal/getInputValidation";
import {
  ContentContainer,
  HeaderText,
  HeaderWrapper,
  ItensWrapper,
  SkeletonWrapper,
  StyledIconButton,
} from "./styles";
import { useAppState } from "Context/StateContext";
import { isShowAction } from "Util/permissions";

type Translate = TFunction<"translation", undefined>;

export interface UploadErrors {
  [x: string]: string[];
}

interface IUploadItemArray {
  name: ColumnsValues;
  label: string;
  translate: Translate;
  isLoading?: boolean;
  status?: number;
}

type Props = {
  permission?: {
    edit: boolean;
  };
};

export const Upload = (props: Props) => {
  const { permission } = props;
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const {
    uploadItemsStatus,
    isUploadCentreItemsStatusLoading,
    isServicesCodesLoading,
    isMaterialsCodesLoading,
  } = useUploadCentreAction();

  const uploadCentreItems: IUploadItemArray[] = [
    {
      name: UploadCentreItemName.MATERIAL,
      label: UploadCentreUploadLabel.LIST_OF_MATERIALS,
      translate,
    },
    {
      name: UploadCentreItemName.STAFF,
      label: UploadCentreUploadLabel.ON_FIELD_STAFF,
      translate,
    },
    {
      name: UploadCentreItemName.FLEET,
      label: UploadCentreUploadLabel.FLEET,
      translate,
    },
    {
      name: UploadCentreItemName.SERVICE,
      label: UploadCentreUploadLabel.SERVICES,
      translate,
    },
  ];

  const isDataLoading =
    isUploadCentreItemsStatusLoading || isServicesCodesLoading || isMaterialsCodesLoading;

  const renderUploadItems = useCallback(() => {
    return uploadCentreItems.map((item) => {
      const matchItemByName = uploadItemsStatus?.labels?.find(
        (itemStatus) => itemStatus.name.toLowerCase() === item.name.toLowerCase()
      );

      if (isDataLoading) {
        return (
          <SkeletonWrapper key={item.label}>
            <SkeletonComponent isLoading={isDataLoading}>
              <div />
            </SkeletonComponent>
          </SkeletonWrapper>
        );
      }

      if (matchItemByName) {
        return (
          <UploadItem
            key={item.label}
            columnName={item.name}
            label={item.label}
            itemLabelId={matchItemByName.id}
            itemStatusKey={matchItemByName?.uploadedBy.key}
            permission={permission}
          />
        );
      }

      return <></>;
    });
  }, [uploadCentreItems]);

  return (
    <OutlinedWrapper>
      <ContentContainer>
        <HeaderWrapper>
          <SkeletonComponent isLoading={isDataLoading}>
            <HeaderText>
              {translate("youCanChooseFromTheListBelowWhichItemsYouWantToUpload")}
            </HeaderText>
          </SkeletonComponent>
          <SkeletonComponent isLoading={isDataLoading}>
            {permission && isShowAction(currentUser, permission?.edit) ? (
              <StyledIconButton
                onClick={() => window.open(env.LINK_UPLOAD_CENTRE_TEMPLATES, "_blank")?.focus()}
              >
                <IconDownFile width={20} height={20} color={colors.white} />
                {translate("downloadCSVTemplate")}
              </StyledIconButton>
            ) : (
              <></>
            )}
          </SkeletonComponent>
        </HeaderWrapper>
        <ItensWrapper>{renderUploadItems()}</ItensWrapper>
      </ContentContainer>
    </OutlinedWrapper>
  );
};
